import React from "react";
import { Outlet, useLocation, useParams } from "react-router-dom";
import { useBulkMessage } from "@/hooks/useBulkMessage";
import { useTranslation } from "react-i18next";
import { FlatTabItem, FlatTab } from "@/components/FlatTab";
import { BulkMessage } from "@/__generated__/graphql";
import { BadgeInfo } from "lucide-react";
import { i18n } from "@/i18n";
import { Button } from "@/components/ui";
import { StopButton } from "./Components/StopButton";
import { useNow } from "@/hooks/useNow";
import { ROUTES } from "@/routes/paths";
import { isActive, isSent } from "./utils/sentStatus";

export interface BulkMessageDetailsContext {
  bulkMessage: Partial<BulkMessage>;
  loading: boolean;
  isSent: boolean;
}

export function BulkMessageDetails() {
  const { t } = useTranslation();
  const { bulkMessageId } = useParams();
  const location = useLocation();
  const now = useNow();

  const { data, loading } = useBulkMessage(bulkMessageId);

  if (loading) {
    return <p>{t("global.loading")}</p>;
  }

  const getActiveTab = () => {
    const path = location.pathname;
    if (
      path.includes(
        ROUTES.COMMUNITY_TOOLS.BULK_MESSAGING.CAMPAIGN_DETAILS ||
          ROUTES.COMMUNITY_TOOLS.BULK_MESSAGING.DETAILS,
      )
    )
      return ROUTES.COMMUNITY_TOOLS.BULK_MESSAGING.CAMPAIGN_DETAILS;
    if (path.includes(ROUTES.COMMUNITY_TOOLS.BULK_MESSAGING.RESULTS))
      return "results";
    return ROUTES.COMMUNITY_TOOLS.BULK_MESSAGING.CAMPAIGN_DETAILS;
  };

  const activeTab = getActiveTab();

  const tabItems: FlatTabItem[] = [
    {
      label: t("bulkMessaging.details.campaignDetails"),
      active:
        activeTab === ROUTES.COMMUNITY_TOOLS.BULK_MESSAGING.CAMPAIGN_DETAILS,
      route: "nowhere",
    },
  ];

  const outletContext = {
    bulkMessage: data,
    loading,
    isSent: data ? isSent(data, now) : true,
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="mb-4 flex w-full items-center pt-6 pr-6 pl-8">
        <h1 className="grow text-2xl font-semibold text-foreground">
          {data?.name ?? t("bulkMessaging.titleBulkMessage")}
        </h1>
        {isActive(data, now) && (
          <StopButton>
            <Button type="button" variant="outline">
              <BadgeInfo />
              {i18n.t("bulkMessaging.table.stop.name")}
            </Button>
          </StopButton>
        )}
      </div>

      <div className="ml-8">
        <FlatTab items={tabItems} />
      </div>

      <div className="w-full border-b border-border"></div>

      <div className="px-8 pt-8 pb-10">
        <Outlet context={outletContext} />
      </div>
    </div>
  );
}
